import { createContext } from 'react';

import { Point, RoomRect, SVG_HEIGHT, SVG_WIDTH } from './mapGeometry';
import { CHAIR_GAP_RATIO, CHAIR_HALF_RATIO } from './tableChairs';

/**
 * ViewBox units per on-screen unit, i.e. `1 / zoom`. MapEditorCanvas provides it; an annotation
 * multiplies its size by it to stay the same size on screen while metre-sized geometry zooms.
 */
export const CanvasUnitContext = createContext(1);

/** A figure's label is never drawn smaller than this on screen. */
export const LABEL_MIN_SCREEN_FONT = 6;
const GRID_MIN_SCREEN_GAP = 16;
/** On-screen extent of the room that must stay in view after panning. */
export const PAN_MIN_VISIBLE = 80;
// Approximate advance of one semi-bold character per unit of font size; SVG text has no layout to measure.
const CHAR_WIDTH_RATIO = 0.68;
const LABEL_WIDTH_FILL = 0.9;
const LABEL_HEIGHT_FILL = 0.8;
const TRUNCATED_MIN_CHARS = 2;
// How far the chairs stand out from the body, as a share of its short side.
const CHAIR_REACH_RATIO = CHAIR_GAP_RATIO + 2 * CHAIR_HALF_RATIO;
const SELECTION_MARGIN = 4;
const HANDLE_FRAME_MIN_SCREEN = 24;

export interface LabelBox {
    width: number;
    height: number;
}

export interface FittedLabel {
    /** ViewBox units. */
    fontSize: number;
    text: string;
}

/**
 * Room an upright label has through the centre of a `width` x `height` rectangle rotated by
 * `rotation` degrees: the horizontal and vertical chords. Dividing by a zero sine or cosine
 * gives Infinity, which `Math.min` discards.
 */
export function uprightLabelBox(width: number, height: number, rotation: number): LabelBox {
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    return {
        width: Math.min(width / cos, height / sin),
        height: Math.min(height / cos, width / sin),
    };
}

function widthFont(charCount: number, box: LabelBox): number {
    return (box.width * LABEL_WIDTH_FILL) / (charCount * CHAR_WIDTH_RATIO);
}

/**
 * A label that stays inside `box`: shrunk from `maxScreenFont` down to the legibility floor, then
 * truncated with an ellipsis at the floor. `null` when not even a truncated one fits.
 */
export function fitLabel(
    text: string,
    box: LabelBox,
    maxScreenFont: number,
    unit: number
): FittedLabel | null {
    // Code points, so a truncation never cuts an emoji in half.
    const chars = Array.from(text);
    if (chars.length === 0) return null;
    const floor = LABEL_MIN_SCREEN_FONT * unit;
    const heightFont = box.height * LABEL_HEIGHT_FILL;
    if (heightFont < floor) return null;
    const fontSize = Math.min(widthFont(chars.length, box), heightFont, maxScreenFont * unit);
    if (fontSize >= floor) return { fontSize, text };
    // The ellipsis takes one of the characters that fit at the floor size.
    const kept = Math.floor(widthFont(1, box) / floor) - 1;
    if (kept < TRUNCATED_MIN_CHARS) return null;
    return { fontSize: floor, text: `${chars.slice(0, kept).join('').trimEnd()}…` };
}

/**
 * Gap between a table body and its selection frame: clears the chairs, and never lets the frame
 * get too small on screen for its handles.
 */
export function selectionPad(figW: number, figH: number, unit: number): number {
    const base = Math.min(figW, figH);
    return Math.max(
        base * CHAIR_REACH_RATIO + SELECTION_MARGIN * unit,
        (HANDLE_FRAME_MIN_SCREEN * unit - base) / 2
    );
}

/** Half-extent of the frame the resize handles sit on: the body's own, unless that is too small on screen. */
export function handleFrameHalf(half: number, unit: number): number {
    return Math.max(half, (HANDLE_FRAME_MIN_SCREEN * unit) / 2);
}

/** Doubles the drawn grid step until its lines are comfortably apart on screen; snapping is not affected. */
export function visibleGridStep(baseStep: number, zoom: number): number {
    let step = baseStep;
    while (step * zoom < GRID_MIN_SCREEN_GAP) step *= 2;
    return step;
}

/**
 * Clamps a pan offset (viewBox units) so at least PAN_MIN_VISIBLE on-screen units of the room stay
 * inside the view. At zoom <= 1 the whole map fits, so there is no pan.
 */
export function clampPan(px: number, py: number, zoom: number, room: RoomRect): Point {
    if (zoom <= 1) return { x: 0, y: 0 };
    const vbW = SVG_WIDTH / zoom;
    const vbH = SVG_HEIGHT / zoom;
    const vbX0 = (SVG_WIDTH - vbW) / 2;
    const vbY0 = (SVG_HEIGHT - vbH) / 2;
    const minVisible = PAN_MIN_VISIBLE / zoom;
    // viewBox left = vbX0 - panX, right = vbX0 - panX + vbW
    const maxPanX = vbX0 + vbW - room.x - minVisible;
    const minPanX = vbX0 - (room.x + room.width) + minVisible;
    const maxPanY = vbY0 + vbH - room.y - minVisible;
    const minPanY = vbY0 - (room.y + room.height) + minVisible;
    return {
        x: Math.min(maxPanX, Math.max(minPanX, px)),
        y: Math.min(maxPanY, Math.max(minPanY, py)),
    };
}
